import { format } from "date-fns";
import { sv } from "date-fns/locale";

interface ReceiptData {
  bookingId: string;
  orgName: string;
  orgAddress?: string | null;
  orgLogoUrl?: string | null;
  courtName: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  pinCode: string;
  players: string[];
  totalAmount: number;
  paidAmount: number;
  paymentStatus: string;
  paymentMethod: string;
  createdAt: string;
}

export function downloadReceipt(data: ReceiptData) {
  const start = new Date(data.startTime);
  const shortId = data.bookingId.slice(0, 8).toUpperCase();

  const statusLabel: Record<string, string> = {
    paid: "Betald",
    manual: "Betald (manuellt)",
    pending: "Väntande",
    unpaid: "Obetald",
  };

  const methodLabel: Record<string, string> = {
    card: "Kort",
    swish: "Swish",
    manual: "Manuell",
    pending: "–",
    free: "Gratis",
  };

  const logoHtml = data.orgLogoUrl
    ? `<img src="${data.orgLogoUrl}" alt="${data.orgName}" style="max-height:48px;max-width:180px;margin-bottom:8px;" />`
    : "";

  const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Kvitto ${shortId}</title>
<style>
  @media print {
    body { margin: 0; }
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #1a1a1a;
    padding: 40px;
    max-width: 600px;
    margin: 0 auto;
  }
  .header { text-align: center; margin-bottom: 32px; border-bottom: 2px solid #e5e5e5; padding-bottom: 24px; }
  .header h1 { font-size: 28px; letter-spacing: 4px; color: #333; margin-top: 8px; }
  .org-name { font-size: 18px; font-weight: 600; color: #555; }
  .org-address { font-size: 12px; color: #888; margin-top: 4px; }
  .section { margin-bottom: 20px; }
  .section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #888; margin-bottom: 8px; font-weight: 600; }
  .row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; }
  .row .label { color: #666; }
  .row .value { font-weight: 500; text-align: right; }
  .divider { border-top: 1px solid #e5e5e5; margin: 16px 0; }
  .total-row { font-size: 18px; font-weight: 700; }
  .pin { font-family: 'Courier New', monospace; font-size: 24px; font-weight: 700; text-align: center; padding: 12px; background: #f5f5f5; border-radius: 8px; margin: 12px 0; letter-spacing: 4px; }
  .footer { text-align: center; margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e5e5; font-size: 11px; color: #aaa; }
  .players { font-size: 13px; color: #444; }
  .players span { display: inline-block; background: #f0f0f0; padding: 2px 8px; border-radius: 4px; margin: 2px 4px 2px 0; }
</style>
</head>
<body>
  <div class="header">
    ${logoHtml}
    <div class="org-name">${data.orgName}</div>
    ${data.orgAddress ? `<div class="org-address">${data.orgAddress}</div>` : ""}
    <h1>KVITTO</h1>
  </div>

  <div class="section">
    <div class="section-title">Bokningsuppgifter</div>
    <div class="row"><span class="label">Boknings-ID</span><span class="value">${shortId}</span></div>
    <div class="row"><span class="label">Datum</span><span class="value">${format(start, "EEEE d MMMM yyyy", { locale: sv })}</span></div>
    <div class="row"><span class="label">Tid</span><span class="value">${format(start, "HH:mm")}–${format(new Date(data.endTime), "HH:mm")} (${data.durationMinutes} min)</span></div>
    <div class="row"><span class="label">Bana</span><span class="value">${data.courtName}</span></div>
  </div>

  <div class="section">
    <div class="section-title">Dörrkod</div>
    <div class="pin">${data.pinCode}</div>
  </div>

  <div class="section">
    <div class="section-title">Spelare</div>
    <div class="players">
      ${data.players.map((n) => `<span>${n}</span>`).join("")}
    </div>
  </div>

  <div class="divider"></div>

  <div class="section">
    <div class="section-title">Betalning</div>
    <div class="row"><span class="label">Belopp</span><span class="value">${data.totalAmount} kr</span></div>
    <div class="row"><span class="label">Betalt</span><span class="value">${data.paidAmount} kr</span></div>
    <div class="row"><span class="label">Status</span><span class="value">${statusLabel[data.paymentStatus] ?? data.paymentStatus}</span></div>
    <div class="row"><span class="label">Betalmetod</span><span class="value">${methodLabel[data.paymentMethod] ?? data.paymentMethod}</span></div>
  </div>

  <div class="divider"></div>
  <div class="row total-row"><span>Totalt</span><span>${data.totalAmount} kr</span></div>

  <div class="footer">
    <p>Skapad: ${format(new Date(data.createdAt), "d MMMM yyyy, HH:mm", { locale: sv })}</p>
    <p style="margin-top:4px;">Genererat från ${data.orgName}</p>
  </div>
</body>
</html>`;

  const printWindow = window.open("", "_blank", "width=650,height=900");
  if (!printWindow) return;
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
}
